import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { invokeAiFunction, kuunda, kuundaErrorMessage } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { normalizeCrmStatus } from '@isbd/shared';
import {
  IconAlertCircle,
  IconBarChart,
  IconLightbulb,
  IconSend,
  IconSparkles,
  IconTrendingUp,
} from '@/features/marketplace-crm/icons';
import '@/features/marketplace-crm/marketplace-crm.css';

type ChatMessage = { role: 'user' | 'assistant'; content: string };

const SUGGESTED = [
  {
    label: 'Analyse Marché',
    text: 'Analyse le marché de mes produits et services. Identifie les tendances actuelles, les opportunités de croissance et les segments de marché les plus prometteurs pour mon activité.',
    Icon: IconTrendingUp,
  },
  {
    label: 'Persona Clients',
    text: 'À partir de mes prospects et clients, construis 2 à 3 personas clients détaillés (besoins, freins, canaux, messages clés) et propose comment adapter mon offre.',
    Icon: IconLightbulb,
  },
  {
    label: 'Benchmark',
    text: 'Propose un benchmark concurrentiel pour mon activité : positionnement, forces/faiblesses relatives, écarts à combler et axes de différenciation.',
    Icon: IconBarChart,
  },
  {
    label: 'Optimisation',
    text: 'Analyse mes performances marketing et ventes (pipeline, conversion, pertes). Propose une stratégie marketing, un plan de construction marketing adapté et des recommandations prioritaires.',
    Icon: IconAlertCircle,
  },
] as const;

function renderLiteMarkdown(text: string): ReactNode {
  const blocks = text.split(/\n{2,}/);
  return blocks.map((block, i) => {
    const lines = block.split('\n');
    if (lines.every((l) => /^\s*[-*]\s+/.test(l))) {
      return (
        <ul key={i}>
          {lines.map((l, j) => (
            <li key={j}>{inlineMd(l.replace(/^\s*[-*]\s+/, ''))}</li>
          ))}
        </ul>
      );
    }
    if (/^#{1,3}\s+/.test(lines[0] ?? '')) {
      const title = (lines[0] ?? '').replace(/^#{1,3}\s+/, '');
      return (
        <div key={i}>
          <strong>{inlineMd(title)}</strong>
          {lines.slice(1).map((l, j) => (
            <p key={j}>{inlineMd(l)}</p>
          ))}
        </div>
      );
    }
    return (
      <p key={i}>
        {lines.map((l, j) => (
          <span key={j}>
            {j > 0 ? <br /> : null}
            {inlineMd(l)}
          </span>
        ))}
      </p>
    );
  });
}

function inlineMd(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

export function AssistantMarketingPage() {
  const { profile } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function buildCrmContext(): Promise<string> {
    if (!profile) return '';
    const [pRes, cRes, oRes] = await Promise.all([
      kuunda.from('crm_leads').select('status, source, opportunity, budget').eq('user_id', profile.id),
      kuunda.from('crm_customers').select('id').eq('user_id', profile.id),
      kuunda.from('crm_offers').select('id').eq('user_id', profile.id),
    ]);

    const prospects = (pRes.data ?? []).map((p) => ({
      stage: normalizeCrmStatus(String(p.status ?? 'nouveau')),
      source: String(p.source || 'manuel'),
      product_interest: String(p.opportunity || ''),
      value: Number(p.budget || 0),
    }));

    const totalProspects = prospects.length;
    const won = prospects.filter((p) => p.stage === 'gagne').length;
    const lost = prospects.filter((p) => p.stage === 'perdu').length;
    const conversionRate = totalProspects > 0 ? ((won / totalProspects) * 100).toFixed(1) : '0';

    const bySource = prospects.reduce<Record<string, number>>((acc, p) => {
      acc[p.source] = (acc[p.source] || 0) + 1;
      return acc;
    }, {});
    const byStage = prospects.reduce<Record<string, number>>((acc, p) => {
      acc[p.stage] = (acc[p.stage] || 0) + 1;
      return acc;
    }, {});
    const topProducts = prospects.reduce<Record<string, number>>((acc, p) => {
      if (p.product_interest) acc[p.product_interest] = (acc[p.product_interest] || 0) + 1;
      return acc;
    }, {});
    const pipelineValue = prospects.reduce((s, p) => s + p.value, 0);

    return `Total prospects: ${totalProspects}
Prospects gagnés: ${won}
Prospects perdus: ${lost}
Taux de conversion: ${conversionRate}%
Nombre de clients: ${(cRes.data ?? []).length}
Offres envoyées: ${(oRes.data ?? []).length}
Répartition par source: ${JSON.stringify(bySource)}
Répartition par étape: ${JSON.stringify(byStage)}
Top produits d'intérêt: ${JSON.stringify(topProducts)}
Valeur totale du pipeline: ${pipelineValue} €`;
  }

  async function send(text: string) {
    if (!text.trim() || loading) return;
    const newMessages: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const crmContext = await buildCrmContext();
      const res = await invokeAiFunction<{ reply?: string; error?: string }>('ai-analyze', {
        kind: 'crm_advisor',
        crm_context: crmContext,
        question: text,
      });
      const reply =
        res.reply?.trim() ||
        res.error ||
        "Désolé, une erreur est survenue lors de l'analyse. Veuillez réessayer.";
      setMessages([...newMessages, { role: 'assistant', content: reply }]);
    } catch (err) {
      setMessages([
        ...newMessages,
        {
          role: 'assistant',
          content: kuundaErrorMessage(err, "Désolé, une erreur est survenue lors de l'analyse. Veuillez réessayer."),
        },
      ]);
    }
    setLoading(false);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void send(input);
  }

  return (
    <div className="u-page mktcrm ai-page">
      <div className="u-hero">
        <div className="ai-title-row">
          <div className="ai-badge">
            <IconSparkles size={20} />
          </div>
          <div>
            <p className="u-subtitle">Analyse marché, personas, benchmark et optimisation commerciale</p>
          </div>
        </div>
        <div className="u-actions">
          <Link to="/app/marketplace-crm" className="btn btn-ghost">
            ← Hub Marketplace & CRM
          </Link>
        </div>
      </div>

      {messages.length === 0 ? (
        <section className="ai-suggest-panel">
          <p className="ai-suggest-intro">Choisissez une analyse ou posez votre propre question :</p>
          <div className="ai-suggest-grid">
            {SUGGESTED.map((p) => (
              <button key={p.label} type="button" className="ai-suggest-card" onClick={() => void send(p.text)}>
                <div className="ai-suggest-label">
                  <p.Icon size={16} />
                  <span>{p.label}</span>
                </div>
                <p>{p.text}</p>
              </button>
            ))}
          </div>
          <div style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={loading}
              onClick={() =>
                void send(
                  'Lance un diagnostic marketing complet : analyse de marché, personas clients, benchmark concurrentiel, performances ventes/pipeline, puis propose une stratégie marketing, un plan de construction marketing adapté et des recommandations prioritaires.',
                )
              }
            >
              Lancer un diagnostic complet
            </button>
          </div>
        </section>
      ) : null}

      <div className="ai-thread">
        {messages.map((m, i) => (
          <div key={i} className={`ai-bubble-row ai-bubble-row--${m.role}`}>
            <div className={`ai-bubble ai-bubble--${m.role}`}>
              {m.role === 'user' ? (
                <p className="ai-bubble-text">{m.content}</p>
              ) : (
                <div className="ai-md">{renderLiteMarkdown(m.content)}</div>
              )}
            </div>
          </div>
        ))}
        {loading ? (
          <div className="ai-bubble-row ai-bubble-row--assistant">
            <div className="ai-bubble ai-bubble--assistant ai-loading-bubble">
              <span className="ai-dots" aria-hidden>
                <i />
                <i />
                <i />
              </span>
              <span>Analyse en cours…</span>
            </div>
          </div>
        ) : null}
        <div ref={endRef} />
      </div>

      <form className="ai-composer" onSubmit={onSubmit}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Posez votre question sur le marché, vos ventes, vos prospects…"
        />
        <button type="submit" className="btn btn-primary" disabled={loading || !input.trim()}>
          <IconSend size={16} /> Envoyer
        </button>
      </form>
    </div>
  );
}
